/* eslint-disable react-hooks/exhaustive-deps */
import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdyenRoutesName } from "routes/route-names";
import { getRoutePath } from "routes/route-utils";
import { AuthPayload, IAuthToken } from "TypesAndInterfaces";
import { displayError, displayWarning } from "utils/displayNotification";
import { PASSWORD_FORMAT } from "utils/validation";
import Button from "../Button/Button";
import InputField from "../InputField/InputField";
import {
  checkPasswordRecoveryRequestQuery,
  recoverPasswordAfterRequestMutation,
} from "./queries";
import * as jose from "jose";

export const ResetPassword = () => {
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { uuid } = useParams();
  const navigate = useNavigate();
  
  const handleSingleUser = (token: string) => {
    const jwt = jose.decodeJwt(token);
    const data = jwt.data as IAuthToken;
    if (data?.marketplaceId) {
      localStorage.setItem("marketplaceId", data.marketplaceId.toString());
      navigate({
        pathname: getRoutePath(AdyenRoutesName.MARKETPLACE_CONTROL),
      });
    }
  };
  
  const [checkRecoverPasswordRequest] = useLazyQuery<
    { checkRecoverPasswordRequest: boolean },
    { uuid: string }
  >(checkPasswordRecoveryRequestQuery, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setValid(data.checkRecoverPasswordRequest);
      if (!data.checkRecoverPasswordRequest) {
        displayWarning("This token is not valid anymore.");
      }
    },
    onError: (error) => {
      displayError(error.message);
    },
  });

  const [recoverPasswordAfterRequest] = useMutation<
    { recoverPasswordAfterRequest: AuthPayload },
    { newPassword: string; uuid: string }
  >(recoverPasswordAfterRequestMutation, {
    onCompleted: (data) => {
      localStorage.setItem(
        "authToken",
        data.recoverPasswordAfterRequest.authToken
      );
      localStorage.setItem(
        "user",
        JSON.stringify(data.recoverPasswordAfterRequest.user)
      );
      handleSingleUser(data.recoverPasswordAfterRequest.authToken);
      navigate(getRoutePath(AdyenRoutesName.DASHBOARD_MARKETPLACES));
    },
    onError: (e) => displayError(e.message),
  });

  const validatePassword = () => {
    const checkPassword = PASSWORD_FORMAT.test(password);
    if (!checkPassword) {
      setValidationErrors([
        "Password must be at least 8 characters long.\nPassword must contain at least one upper case character.\nPassword must contain at least one number character.\nPassword must contain at least one of special characters: `!@#$%^&*()_+-=[]{};':\"\\|,.<>/?~",
      ]);
      return false;
    } else {
      setValidationErrors([]);
    }
    return true;
  };

  const handleSubmit = () => {
    if (password === passwordConfirmation && uuid && validatePassword()) {
      recoverPasswordAfterRequest({
        variables: {
          newPassword: password,
          uuid,
        },
      });
    }
  };

  useEffect(() => {
    if (uuid) {
      checkRecoverPasswordRequest({ variables: { uuid } });
    }
  }, [uuid]);

  return (
    <form className="login__form">
      <InputField
        label="New Password"
        type="password"
        required
        placeholder="Enter your new password"
        autoComplete="new-password"
        autoFocus={true}
        onChange={(e: any) => setPassword(e.target.value)}
        value={password}
        showHideButton
        errors={validationErrors}
        onBlur={validatePassword}
      />
      <InputField
        label="Confirm Password"
        type="password"
        required
        placeholder="Enter your password again"
        autoComplete="new-password"
        className={password !== passwordConfirmation ? "invalid-input" : ""}
        onChange={(e: any) => setPasswordConfirmation(e.target.value)}
        value={passwordConfirmation}
        showHideButton
      />
      <Button
        type="button"
        className="button--primary button--block"
        content="Submit"
        disabled={!valid}
        onClick={() => handleSubmit()}
      ></Button>
      <Button
        type="button"
        className="button--secondary button--block"
        content="Back to Login"
        icon={""}
        onClick={() => {
          navigate(getRoutePath(AdyenRoutesName.LOGIN));
        }}
      ></Button>
    </form>
  );
};
